import { cn } from '@/lib/cn';
import { type LucideIcon, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';

/**
 * Responsywny modal z native `<dialog>` + showModal().
 *
 * Mobile-first sizing — `max-w-[95vw] max-h-[90vh]` BAZA, sm: nadpisuje
 * konfigurowalnym `size`-em. Content scrolluje się w środku gdy długi.
 *
 * Dlaczego native <dialog>:
 *  - Wbudowany backdrop (`::backdrop`)
 *  - Native ESC close
 *  - Nesting w przeglądarkach automatyczny (modal w modalu)
 *  - A11y: focus trap, ARIA dialog role
 *
 * Mobile UX:
 *  - Auto-`scrollIntoView({block:'center'})` na focus inputa — klawiatura
 *    wirtualna iOS/Android nie zasłania aktywnego pola.
 *  - Body scroll lock przez native <dialog> (przegladarka robi to za nas).
 *  - Backdrop click zamyka (jeśli `dismissOnBackdrop` true, default).
 *
 * UŻYCIE:
 *   <ResponsiveModal open={open} onClose={onClose} size="lg" title="...">
 *     <div>Content</div>
 *   </ResponsiveModal>
 */

type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'preview' | 'fullscreen';

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'sm:max-w-sm', // 384px
  md: 'sm:max-w-md', // 448px
  lg: 'sm:max-w-lg', // 512px
  xl: 'sm:max-w-2xl', // 672px
  '2xl': 'sm:max-w-4xl', // 896px
  // Podglad PDF/zdjecia: stala wysokosc, tresc wypelnia srodek (flex).
  preview: 'h-[92dvh] max-h-[92dvh] sm:max-w-5xl open:flex open:flex-col',
  fullscreen: 'sm:max-w-[95vw] sm:max-h-[95vh]',
};

export function ResponsiveModal({
  open,
  onClose,
  title,
  children,
  size = 'lg',
  dismissOnBackdrop = true,
  showCloseButton = true,
  footer,
  className,
  icon: Icon,
  subtitle,
  headerActions,
  bodyClassName,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Tytul modala. String renderuje sie jako `<h2 text-base font-semibold>`.
   * ReactNode pozwala wrzucic icon + tytul + subtitle (np. lead.name).
   * Wzorzec custom title:
   *   <div className="flex items-center gap-3">
   *     <div className="h-9 w-9 rounded-full bg-accent-subtle flex items-center justify-center">
   *       <Icon className="h-4 w-4 text-accent" />
   *     </div>
   *     <div>
   *       <h2 className="text-base font-semibold text-fg">Tytul</h2>
   *       <p className="text-xs text-fg-muted">Subtitle</p>
   *     </div>
   *   </div>
   */
  title?: string | ReactNode;
  children: ReactNode;
  size?: Size;
  dismissOnBackdrop?: boolean;
  showCloseButton?: boolean;
  footer?: ReactNode;
  className?: string;
  /** Ikona w kolku obok tytulu — jeden wyglad naglowka we wszystkich oknach. */
  icon?: LucideIcon;
  /** Druga linia pod tytulem (np. nazwa klienta, rozmiar pliku). */
  subtitle?: ReactNode;
  /** Przyciski w naglowku PRZED zamknieciem (np. Pobierz, Otworz w nowej karcie). */
  headerActions?: ReactNode;
  /** Nadpisanie paddingu tresci (np. 'p-0' dla podgladu PDF). */
  bodyClassName?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // showModal/close synchronizacja z prop `open`
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() daje fokus pierwszemu elementowi (✕ w naglowku), a React-owe
      // autoFocus pol odpalilo sie wczesniej, na zamknietym dialogu. Na komputerze
      // stawiamy kursor w pierwszym polu; na dotyku nie — nie wysuwamy klawiatury.
      if (!window.matchMedia('(pointer: coarse)').matches) {
        contentRef.current
          ?.querySelector<HTMLElement>(
            'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
          )
          ?.focus({ preventScroll: true });
      }
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Native dialog dispatcha 'close' event (np. po ESC) — re-router do parent onClose
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const onNativeClose = () => {
      if (open) onClose();
    };
    dialog.addEventListener('close', onNativeClose);
    return () => dialog.removeEventListener('close', onNativeClose);
  }, [open, onClose]);

  // Backdrop click → close (event.target === dialog gdy klikamy w ::backdrop)
  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (!dismissOnBackdrop) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Auto scroll-into-view na focus inputów w środku modala (iOS keyboard fix).
  //
  // TYLKO dotyk (pointer: coarse) — na desktopie nie ma klawiatury zaslaniajacej
  // pola, wiec kazdy taki scroll to wylacznie szkoda. Wczesniej lecial wszedzie
  // i przy edytorze umowy (contenteditable wysoki na kilka tys. px) `block:
  // 'center'` przewijal w SRODEK dokumentu — user klikal "edycja tresci"
  // i ladowal na dole umowy (zglosil Dawid 2026-07-17).
  useEffect(() => {
    if (!open) return;
    const content = contentRef.current;
    if (!content) return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Tylko form fields, nie buttony/divy. ContentEditable POMIJAMY —
      // przegladarka sama trzyma karetke w kadrze, a scroll calego elementu
      // wyrzuca usera w losowe miejsce dlugiego dokumentu.
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        // Małe opóźnienie — czekamy aż klawiatura mobilna sie rozwinie
        window.setTimeout(() => {
          // 'nearest' zamiast 'center' — minimalny scroll, zero skoku gdy pole
          // juz jest widoczne.
          target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 250);
      }
    };

    content.addEventListener('focusin', onFocusIn);
    return () => content.removeEventListener('focusin', onFocusIn);
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      onClick={handleClick}
      className={cn(
        // CENTROWANIE: realizowane w globals.css (`dialog[open]` reset UA
        // + transform: translate(-50%, -50%)). UA margin:auto NIE centruje
        // pionowo na block elementach z content-based height (CSS spec
        // §10.6.4). Tu nie dodajemy klas centrujacych zeby nie pogorszyc.
        // Mobile-first: 95vw / 90vh, scroll content w środku
        'max-h-[90vh] w-full max-w-[95vw] rounded-lg border border-border bg-surface p-0 text-fg shadow-lg',
        // Kolumna: nagłówek i stopka stoją, przewija się tylko środek — inaczej przewijał się cały
        // <dialog> i przyklejone elementy treści (pasek edytora) wchodziły pod/na nagłówek.
        'open:flex open:flex-col',
        // sm+: konfigurowany rozmiar
        SIZE_CLASSES[size],
        // Backdrop styling
        'backdrop:bg-bg/80 backdrop:backdrop-blur-sm',
        className,
      )}
    >
      {/* Naglowek — WSZYSTKIE okna tak samo (Dawid 2026-09-30: „zamknij jest znowu
          gdzies indziej”): [ikona] tytul/podtytul … akcje · ✕ zawsze w prawym gornym rogu. */}
      {(title || showCloseButton) && (
        <div className="sticky top-0 z-10 flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-3 sm:px-5">
          {Icon && (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent">
              <Icon className="h-4 w-4" strokeWidth={2} />
            </span>
          )}
          <div className="min-w-0 flex-1">
            {typeof title === 'string' ? (
              <h2 className="truncate text-base font-semibold text-fg">{title}</h2>
            ) : (
              title
            )}
            {subtitle && <p className="truncate text-xs text-fg-muted">{subtitle}</p>}
          </div>
          {headerActions && <div className="flex shrink-0 items-center gap-1">{headerActions}</div>}
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg active:bg-surface-3"
              aria-label="Zamknij"
              title="Zamknij (Esc)"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {/* Content (scrollable area) */}
      <div
        ref={contentRef}
        className={cn(
          'min-h-0 flex-1 overflow-y-auto px-5',
          // Odstęp góra/dół jako elementy treści, nie padding: przeglądarka przykleja sticky
          // do krawędzi treści (bez paddingu), więc przy py-4 pasek edytora wisiał 16 px pod
          // nagłówkiem i prześwitywał nad nim tekst. Okna z własnym układem dają p-0.
          !bodyClassName?.includes('p-0') &&
            "before:block before:h-4 before:content-[''] after:block after:h-4 after:content-['']",
          bodyClassName,
        )}
      >
        {children}
      </div>

      {/* Footer (sticky bottom dla stick-z-actions na mobile) */}
      {footer && (
        <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 sm:px-5">
          {footer}
        </div>
      )}
    </dialog>
  );
}
